import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
}

export const useAuth = (): AuthState => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    // Get initial session
    const getInitialSession = async () => {
      if (!supabase) return;
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        await checkAdminRole(session.user.email!);
      }

      setLoading(false);
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: string, session: Session | null) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          await checkAdminRole(session.user.email!);
        } else {
          setIsAdmin(false);
        }

        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminRole = async (email: string) => {
    if (!supabase) return;

    // Bypass database check for known admin email to prevent auth loops
    if (email === 'george.mcpherson@rightawaygroup.com') {
      console.log('Known admin email detected, setting admin=true immediately');
      setIsAdmin(true);
      return;
    }

    try {
      // For other users, check database
      const { data: profile, error } = await supabase
        .from('app_users')
        .select('role')
        .eq('email', email)
        .maybeSingle();

      if (error || !profile) {
        console.warn('Error checking admin role or user not found:', error);
        setIsAdmin(false);
        return;
      }

      setIsAdmin(profile.role === 'admin' || profile.role === 'editor' || profile.role === 'manager');
    } catch (error) {
      console.error('Error checking admin role:', error);
      setIsAdmin(false);
    }
  };

  return { user, session, loading, isAdmin };
};
